<script setup>
import { ref, computed } from 'vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { generateInvoicePDFv2 } from '@/utils/downloads.js'

const search = ref(''); const stageFilter = ref('All')
const drawerOrderNo = ref(null)
const paymentNote = ref('')

const invoiced = computed(() => store.pharmacyV2.orders.value.filter(o => o.invoice))
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return invoiced.value.filter(o => {
    const matchesQ = !q || o.orderNo.toLowerCase().includes(q) || (o.invoice.invoiceNo || '').toLowerCase().includes(q)
    const matchesStage = stageFilter.value === 'All' || o.invoice.billingStage === stageFilter.value
    return matchesQ && matchesStage
  })
})
const stageOptions = computed(() => ['All', ...new Set(invoiced.value.map(o => o.invoice.billingStage))])

const drawerOrder = computed(() => drawerOrderNo.value ? invoiced.value.find(o => o.orderNo === drawerOrderNo.value) || null : null)
const canPay = computed(() => drawerOrder.value && drawerOrder.value.invoice.billingStage !== 'Payment Received')

function openDrawer(o) { drawerOrderNo.value = o.orderNo; paymentNote.value = '' }
function download(o) { generateInvoicePDFv2(o, store.pharmacyV2.profile) }
function payViaPlatform() { if (drawerOrderNo.value) store.pharmacyV2.payViaPlatform(drawerOrderNo.value) }
function recordAsPaid() {
  if (!drawerOrderNo.value) return
  store.pharmacyV2.recordPaymentAsPaid(drawerOrderNo.value, paymentNote.value.trim())
  paymentNote.value = ''
}
</script>

<template>
  <div>
    <div class="card module-header"><h2>{{ invoiced.length }} invoices</h2></div>

    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search order no. or invoice no.…" />
        <select class="filter-select" v-model="stageFilter"><option v-for="s in stageOptions" :key="s">{{ s }}</option></select>
        <span class="result-count">{{ filtered.length }} of {{ invoiced.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Invoice No.</th><th>Order No.</th><th>Date</th><th>Value</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="o in filtered" :key="o.orderNo" class="clickable" @click="openDrawer(o)">
              <td class="cell-strong">{{ o.invoice.invoiceNo || '—' }}</td>
              <td class="cell-muted">{{ o.orderNo }}</td>
              <td class="cell-muted">{{ o.invoice.invoiceDate || '—' }}</td>
              <td class="cell-muted">${{ o.invoice.invoiceValue.toLocaleString() }}</td>
              <td>
                <span class="status-pill" :class="o.invoice.billingStage==='Payment Received' ? 'green' : 'yellow'"><span class="dot"></span>{{ o.invoice.billingStage }}</span>
              </td>
              <td><button class="filter-select" style="cursor:pointer;font-size:11px;" @click.stop="download(o)">Download PDF</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerOrder" :title="drawerOrder?.invoice.invoiceNo || drawerOrder?.orderNo || ''" @close="drawerOrderNo=null">
      <template v-if="drawerOrder">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Order No.</div>{{ drawerOrder.orderNo }}</div>
          <div><div class="label">Invoice Date</div>{{ drawerOrder.invoice.invoiceDate || 'Not yet generated' }}</div>
          <div><div class="label">Status</div>
            <span class="status-pill" :class="drawerOrder.invoice.billingStage==='Payment Received' ? 'green' : 'yellow'"><span class="dot"></span>{{ drawerOrder.invoice.billingStage }}</span>
          </div>
        </div>

        <table class="data-table" style="margin-bottom:18px;">
          <thead><tr><th>Description</th><th style="text-align:right;">Amount (USD)</th></tr></thead>
          <tbody>
            <tr><td>Base Value</td><td class="cell-muted" style="text-align:right;">${{ drawerOrder.invoice.baseValue.toLocaleString() }}</td></tr>
            <tr><td>Zimbabwe VAT (15%)</td><td class="cell-muted" style="text-align:right;">${{ drawerOrder.invoice.taxAmount.toLocaleString() }}</td></tr>
            <tr><td class="cell-strong">Total Invoice Value</td><td class="cell-strong" style="text-align:right;">${{ drawerOrder.invoice.invoiceValue.toLocaleString() }}</td></tr>
          </tbody>
        </table>

        <button class="filter-select" style="cursor:pointer;margin-bottom:16px;" @click="download(drawerOrder)">Download PDF Invoice</button>

        <div v-if="drawerOrder.invoice.paymentMethod" class="oos-remarks" style="margin-bottom:16px;">
          <strong style="display:block;font-size:11px;text-transform:uppercase;color:var(--color-text-muted);margin-bottom:3px;">Payment</strong>
          {{ drawerOrder.invoice.paymentMethod }}{{ drawerOrder.invoice.paymentDate ? ' — '+drawerOrder.invoice.paymentDate : '' }}
          <div v-if="drawerOrder.invoice.paymentNote">{{ drawerOrder.invoice.paymentNote }}</div>
        </div>

        <div v-if="canPay" style="border-top:1px solid var(--color-border);padding-top:16px;">
          <button class="btn-primary" style="margin-right:8px;" @click="payViaPlatform">Pay via Platform</button>
          <div style="display:flex;gap:8px;margin-top:10px;">
            <input class="form-input" v-model="paymentNote" placeholder="Note (e.g. bank ref)" />
            <button class="filter-select" style="cursor:pointer;" @click="recordAsPaid">Record Payment as Paid</button>
          </div>
        </div>
      </template>
    </DetailDrawer>
  </div>
</template>
